import * as styles from "./TimePickerColumn.css";

import type { JSX, KeyboardEvent } from "react";
import { useEffect, useRef } from "react";

import {
  ScrollArea,
  ScrollAreaContent,
  ScrollAreaScrollbar,
  ScrollAreaThumb,
  ScrollAreaViewport,
} from "@frontend/components/atoms/ScrollArea";

import { useTimePickerContext } from "./TimePicker.context";
import type { TimePickerColumnProps } from "./TimePicker.types";
import { TimePickerItem } from "./TimePickerItem";

function centerItem(viewport: HTMLElement, item: HTMLElement): void {
  const viewportRect = viewport.getBoundingClientRect();
  const itemRect = item.getBoundingClientRect();
  const delta =
    itemRect.top +
    itemRect.height / 2 -
    (viewportRect.top + viewportRect.height / 2);

  viewport.scrollTop += delta;
}

export function TimePickerColumn({
  items,
  selected,
  onSelect,
  "aria-label": ariaLabel,
}: TimePickerColumnProps): JSX.Element {
  const { disabled } = useTimePickerContext();
  const viewportRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    const item = listRef.current?.querySelector<HTMLElement>(
      `[data-value="${selected}"]`,
    );

    if (viewport && item) {
      centerItem(viewport, item);
    }
  }, [selected]);

  function selectAndCenter(value: number, focus = false) {
    onSelect(value);

    const viewport = viewportRef.current;
    const item = listRef.current?.querySelector<HTMLButtonElement>(
      `[data-value="${value}"]`,
    );

    if (focus) {
      item?.focus({ preventScroll: true });
    }

    if (viewport && item) {
      centerItem(viewport, item);
    }
  }

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const currentIndex = items.indexOf(selected);

    let nextIndex: number;

    if (event.key === "ArrowDown") {
      nextIndex = Math.min(currentIndex + 1, items.length - 1);
    } else if (event.key === "ArrowUp") {
      nextIndex = Math.max(currentIndex - 1, 0);
    } else if (event.key === "Home") {
      nextIndex = 0;
    } else if (event.key === "End") {
      nextIndex = items.length - 1;
    } else {
      return;
    }

    event.preventDefault();

    selectAndCenter(items[nextIndex], true);
  }

  return (
    <ScrollArea className={styles.column}>
      <ScrollAreaViewport ref={viewportRef} fade>
        <ScrollAreaContent
          ref={listRef}
          role="listbox"
          aria-label={ariaLabel}
          aria-disabled={disabled}
          className={styles.list}
          onKeyDown={handleKeyDown}
        >
          {items.map((item) => (
            <TimePickerItem
              key={item}
              value={item}
              label={String(item).padStart(2, "0")}
              selected={item === selected}
              onSelect={selectAndCenter}
            />
          ))}
        </ScrollAreaContent>
      </ScrollAreaViewport>
      <ScrollAreaScrollbar>
        <ScrollAreaThumb className={styles.thumb} />
      </ScrollAreaScrollbar>
    </ScrollArea>
  );
}
